import React, { createContext, useCallback, useEffect, useState } from 'react';
import Sdk from 'casdoor-js-sdk';
import { Loader } from '../../components/Loader';
import { CasdoorLoginPage } from '../../components/CasdoorLoginPage';
import { useQueryState } from '../../hooks/useQueryState';
import { useLocation } from 'react-router-dom';

// Casdoor configuration from environment variables
const casdoorConfig = {
  serverUrl: import.meta.env.VITE_CASDOOR_URL || '',
  clientId: import.meta.env.VITE_CASDOOR_CLIENT_ID || '',
  appName: import.meta.env.VITE_CASDOOR_APP_NAME || '',
  organizationName: import.meta.env.VITE_CASDOOR_ORG_NAME || '',
  redirectPath: import.meta.env.VITE_CASDOOR_REDIRECT_PATH || '/callback',
};

// Initialize Casdoor SDK
const casdoorSdk = new Sdk(casdoorConfig);

export interface CasdoorUser {
  name: string;
  displayName?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  roles?: string[];
  externalId?: string;
  [key: string]: any;
}

export const CasdoorContext = createContext({
  user: undefined as undefined | null | CasdoorUser,
  role: undefined as undefined | string,
  casdoorSdk: casdoorSdk,
  logout: undefined as undefined | ((shouldConfirm?: boolean) => Promise<void>),
});

// Paths that do not require login (e.g. privacy policy for app store links)
const PUBLIC_PATHS = ['/privacy', '/callback', '/download'];

function nestAuthErrorMessage(
  body: { message?: string | string[]; msg?: string },
  fallback: string,
): string {
  const raw = body.message ?? body.msg;
  if (Array.isArray(raw)) return raw.join('; ');
  if (typeof raw === 'string' && raw.trim()) return raw;
  return fallback;
}

export const CasdoorContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CasdoorUser | null | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [oauthGateError, setOauthGateError] = useState<string | null>(null);
  const clearOauthGateError = useCallback(() => setOauthGateError(null), []);
  const [invitationCode] = useQueryState('ci', '');
  const location = useLocation();

  useEffect(() => {
    // Check if we're in WeChat browser
    const isWeChatBrowser = /MicroMessenger/i.test(navigator.userAgent);
    
    if (isWeChatBrowser) {
      // Handle WeChat OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      
      if (code && state) {
        // WeChat OAuth callback handling
        console.log('WeChat OAuth callback detected');
        // Note: Backend needs to exchange code for token
      } else {
        // Redirect to WeChat OAuth if not authenticated
        const wechatIdp = import.meta.env.VITE_CASDOOR_WECHAT_IDP || 'WeChat';
        const wechatAuthUrl = `${casdoorConfig.serverUrl}/login/oauth/authorize?client_id=${casdoorConfig.clientId}&response_type=code&redirect_uri=${encodeURIComponent(window.location.origin + window.location.pathname)}&scope=read&state=wechat&idp=${wechatIdp}`;
        // Uncomment to enable automatic WeChat OAuth redirect
        // window.location.href = wechatAuthUrl;
      }
    }

  const clearAllAuthStorage = () => {
    localStorage.removeItem('casdoorToken');
    localStorage.removeItem('casdoorRefreshToken');
    localStorage.removeItem('casdoorUser');
    localStorage.removeItem('_authing_token');
    localStorage.removeItem('CurrentSchoolId');
    localStorage.removeItem('CurrentClassId');
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('_authing')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  };

  // Check authentication status
  const checkAuth = async () => {
    try {
      // Detect and clear legacy Authing tokens that have no corresponding Casdoor token
      const hasLegacyAuthingToken = localStorage.getItem('_authing_token');
      const hasCasdoorToken = localStorage.getItem('casdoorToken');

      if (hasLegacyAuthingToken && !hasCasdoorToken) {
        console.warn('[Auth] Legacy Authing token detected without Casdoor token, clearing');
        clearAllAuthStorage();
        setUser(null);
        return;
      }

      // Check if we have code in URL (OAuth callback)
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      
      if (code && state) {
        // OAuth callback detected
        
        try {
          // Call backend API to exchange code for token
          // Use relative path - Vite proxy will forward to backend in dev mode
          // In production, this should be handled by nginx or similar reverse proxy
          const response = await fetch('/api/auth/casdoor/callback', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code, state }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const msg = nestAuthErrorMessage(errorData, `HTTP ${response.status}`);
            clearAllAuthStorage();
            setUser(null);
            setOauthGateError(msg);
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }

          const { accessToken, refreshToken, user } = await response.json();

          setOauthGateError(null);

          localStorage.setItem('casdoorToken', accessToken);
          localStorage.setItem('_authing_token', accessToken);
          localStorage.setItem('casdoorUser', JSON.stringify(user));
          if (refreshToken) {
            localStorage.setItem('casdoorRefreshToken', refreshToken);
          }

          // Set user state
          setUser(user as CasdoorUser);
          
          // Determine role from user roles
          const isTeacher = user.roles?.some((r: any) => {
            // Handle both string and object roles
            const roleName = typeof r === 'string' ? r : r.name;
            return roleName === 'teacher' || roleName.includes('teacher');
          });
          setRole(isTeacher ? 'teacher' : 'student');

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);

        } catch (error) {
          console.error('Casdoor: OAuth callback failed:', error);
          const msg = error instanceof Error ? error.message : 'Unknown error';
          clearAllAuthStorage();
          setUser(null);
          setOauthGateError(msg.includes('系统未开通') ? msg : `登录失败：${msg}`);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        return;
      }

      // Try to get token from localStorage
      const token = localStorage.getItem('casdoorToken');
      
      if (token) {
        // Validate token expiry before trusting it
        try {
          // JWT uses Base64URL encoding (- instead of +, _ instead of /)
          let b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          const pad = b64.length % 4;
          if (pad) b64 += '='.repeat(4 - pad);
          const payload = JSON.parse(atob(b64));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            console.warn('[Auth] Token expired, clearing');
            clearAllAuthStorage();
            setUser(null);
            return;
          }
        } catch {
          console.warn('[Auth] Invalid token format, clearing');
          clearAllAuthStorage();
          setUser(null);
          return;
        }

        try {
          const userInfo = JSON.parse(localStorage.getItem('casdoorUser') || 'null');
          
          if (userInfo) {
            setUser(userInfo as CasdoorUser);
            
            // Determine role based on Casdoor roles
            const userRoles = userInfo.roles || [];
            const isTeacher = userRoles.some((r: string) => r === 'teacher' || r.includes('teacher'));
            setRole(isTeacher ? 'teacher' : 'student');
            
            console.info(`[Auth] Logged in as ${userInfo.displayName || userInfo.name} (${userInfo.externalId})`);
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error('[Auth] Failed to parse user info:', error);
          clearAllAuthStorage();
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      setUser(null);
    }
  };

    checkAuth();
  }, []);

  // Handle invitation code if present
  useEffect(() => {
    if (invitationCode && user) {
      localStorage.setItem('ci', invitationCode);
      // Additional invitation handling logic here
    }
  }, [invitationCode, user]);

  const logout = async (shouldConfirm: boolean = true) => {
    if (shouldConfirm) {
      const confirmed = window.confirm('Are you sure you want to logout?');
      if (!confirmed) return;
    }
    
    try {
      // Clear local storage
      localStorage.removeItem('ci');
      localStorage.removeItem('casdoorToken');
      localStorage.removeItem('casdoorRefreshToken');
      localStorage.removeItem('_authing_token');
      localStorage.removeItem('casdoorUser');
      localStorage.removeItem('CurrentSchoolId');
      localStorage.removeItem('CurrentClassId');
      
      // Directly redirect to frontend login page instead of Casdoor logout
      // This prevents the redirect to port 8000
      window.location.assign(window.location.origin);
    } catch (error) {
      console.error('Logout failed:', error);
      // Force redirect anyway
      window.location.assign(window.location.origin);
    }
  };

  const isPublicPath = PUBLIC_PATHS.some(
    (p) => location.pathname === p || location.pathname.endsWith(p)
  );

  // On public paths, render immediately without waiting for auth
  if (isPublicPath) {
    return (
      <CasdoorContext.Provider value={{ user: user ?? null, role, casdoorSdk, logout }}>
        {children}
      </CasdoorContext.Provider>
    );
  }

  // Show loader while checking authentication
  if (user === undefined) {
    return <Loader fullscreen source="CasdoorAuthProvider" />;
  }

  // Show login page if not authenticated
  if (user === null) {
    const handleWeChatLogin = () => {
      // Redirect to WeChat OAuth login
      const redirectUri = window.location.origin + '/callback';
      const wechatIdp = import.meta.env.VITE_CASDOOR_WECHAT_IDP || 'WeChat';
      const wechatAuthUrl = `${casdoorConfig.serverUrl}/login/oauth/authorize?client_id=${casdoorConfig.clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=profile&state=wechat&idp=${wechatIdp}`;
      
      console.log('Redirecting to WeChat login:', wechatAuthUrl);
      window.location.href = wechatAuthUrl;
    };

    return (
      <CasdoorLoginPage
        onWeChatLogin={handleWeChatLogin}
        oauthGateError={oauthGateError}
        onOauthGateErrorConsumed={clearOauthGateError}
      />
    );
  }

  return (
    <CasdoorContext.Provider value={{ user, role, casdoorSdk, logout }}>
      {children}
    </CasdoorContext.Provider>
  );
};

// Hook to use Casdoor context
export const useCasdoor = () => {
  const context = React.useContext(CasdoorContext);
  if (context === undefined) {
    throw new Error('useCasdoor must be used within CasdoorContextProvider');
  }
  return context;
};

